import React, { Fragment, useEffect, useState } from "react";
import { ShowHide } from "components/layout";
import { AlertUnsavedData, NotificationInline } from "components/notifications";
import { UserAccess, UserHasAccess } from "components/user-access";
import { useDirtyData } from "hooks/useDirtyData";
import { useValidation } from "hooks/useValidation";
import { PageRestricted } from "pages/errors/page-restricted";
import PropTypes from "prop-types";
import { DefaultEventTypeChecksModel } from "pages/fleet/fleet-event-type/wizard/shared/DefaultEventTypeModel";
import { FormSwitch, FormCheckbox } from "components/form-fields";
import { ListingTable } from "components/listing";
import { CheckService } from "services/CheckService";
import { AssetService } from "services";
import { ButtonSave } from "components/buttons";
import { useNavigate } from "react-router-dom";
import { TemplateSubCategories } from "pages/checks/constants";

export function EventTypeChecks({
    model = DefaultEventTypeChecksModel,
    forceLoad = false,
    detailsModel,
    scheduleModel,
    isInWizardMode = true,
    saving = false,
    globalLoading = false,
    onValidationUpdate = () => { },
    onModelUpdate = () => { return DefaultEventTypeChecksModel; },
    onSave = () => { },
    onForceLoadFinished = () => { }
}) {
    var navigate = useNavigate();

    const step = 6;
    const fmsManageAction = "FMS:Manage";
    const userHasFmsManageAction = UserHasAccess(fmsManageAction);
    
    const [loading, setLoading] = useState(true);

    const [originalChecksModel, setOriginalChecksModel] = useState(DefaultEventTypeChecksModel);
    const [checksModel, setChecksModel] = useState(DefaultEventTypeChecksModel)

    const [isValid, , validateChecksModel] = useValidation();

    const [tableRows, setTableRows] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const isChecksModelDirty = useDirtyData(originalChecksModel, checksModel);

    const [assetTypes, setAssetTypes] = useState([]);
    const defaultPageSize = 25;
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [accountUsesSubType, setAccountUsesSubType] = useState(true);
    const assetSubTypeLabel = "Asset Sub-Types";
    const [tableHeaders, setTableHeaders] = useState([
        "Name",
        "Version",
        "Asset Types",
        assetSubTypeLabel,
        "Select"
    ]);
    const [specialProcessing, setSpecialProcessing] = useState(false);
    const [specialProcessingMessage, setSpecialProcessingMessage] = useState(null);

    const sortField = "name";
    const sortDirection = "A";

    useEffect(() => {
        AssetService.getAssetOptions()
            .then(assetOptions => {
                setAssetTypes(assetOptions.assetTypes || []);
            })
            .catch(() => {
                setAssetTypes([]);
            })
            .finally(() => {
                setLoading(false)
            });
    }, []);

    useEffect(() => {
        if (model?.specialProcessingKey && model?.specialProcessingKey.toUpperCase() !== 'PMI') {
            setSpecialProcessing(true);
            if (model.specialProcessingKey.toUpperCase() === 'WALKAROUND') {
                setSpecialProcessingMessage('This event type will be created whenever an associated asset has a walkaround created which contains a defect.');
            } else {
                setSpecialProcessingMessage('This event type is not permitted to have a related check added.');
            }
        }
    }, [model.specialProcessingKey]);

    useEffect(() => {
        if (!userHasFmsManageAction || (forceLoad === false && loading === false)) {
            return;
        }

        setModel(prev => { return {...prev, ...model}; });

        if (!isInWizardMode) {
            setOriginalChecksModel(model);
        }

        if (forceLoad) {
            onForceLoadFinished();
        }
        searchChecks();
    }, [model, forceLoad, userHasFmsManageAction, isInWizardMode, loading]);



    useEffect(() => {
        setLoading(globalLoading);
    }, [globalLoading]);

    // publish out validation changes if in wizard mode
    useEffect(() => {
        if (!isInWizardMode) {
            return;
        }

        onValidationUpdate(step, isValid);
    }, [isValid])

    useEffect(validate, [checksModel])

    useEffect(() => {
        if (assetTypes && assetTypes.length > 0 && accountUsesSubType) {
            let usesSubType = false;
            assetTypes.forEach(type => {
                if (type.subtypes && type.subtypes.length > 0) {
                    usesSubType = true;
                }
            });
            if (!usesSubType) {
                setAccountUsesSubType(false);
                let position = tableHeaders.indexOf(assetSubTypeLabel);
                if (position && position > -1) {
                    setTableHeaders((prev) => {
                        let clone = [...prev];
                        clone.splice(position, 1);
                        return clone;
                    });
                }
            }
        }
    }, [assetTypes, accountUsesSubType]);

    function setConditionFilters(conditionArray, key, filters) {
        if (conditionArray !== undefined && conditionArray !== null && Array.isArray(conditionArray) && conditionArray.length > 0) {
            conditionArray.forEach((item) => {
                filters.push({
                    key: key,
                    value: item.toString()
                });
            });
        }
        else {
            //This will make the filters search for the "All" condition, where nothing is set
            filters.push({
                key: key,
                value: ""
            });
        }
    }

    function buildFilters() {
        const baseFilters = [
            {
                key: "category.categoryname",
                name: "category.categoryname",
                displayName: "Asset",
                value: "Asset"
            },
            {
                key: "templatestatus.templatestatusname",
                name: "templatestatus.templatestatusname",
                displayName: "Published",
                value: "Published"
            }
        ];

        const conditions = detailsModel?.additionalProperties?.conditions || {};
        const dynamicFilters = [];

        setConditionFilters(conditions.assetTypes, "Conditions.AssetTypes", dynamicFilters);
        setConditionFilters(conditions.assetSubTypes, "Conditions.AssetSubTypes", dynamicFilters);
        setConditionFilters(conditions.assetOwnerships, "Conditions.AssetOwnerships", dynamicFilters);
        setConditionFilters(conditions.assetStatuses, "Conditions.AssetStatuses", dynamicFilters);

        return [...baseFilters, ...dynamicFilters];
    }

    function validateSelectedChecks(templates) {
        //Make sure all selected templates fall within the returned templates
        if (!Array.isArray(model.relatedTemplates) || model.relatedTemplates.length === 0) {
            return;
        }

        const value = model.relatedTemplates.filter(relatedCheck => {
            return templates.some(template => template.templateId === relatedCheck.id);
        });

        if (value.length === model.relatedTemplates.length) {
            return;
        }

        onValueChange("relatedTemplates", value);
    };

    function searchChecks() {
        setIsLoadingData(true);
        const searchObject = {
            requestCount: pageSize,
            pageNumber: pageNumber,
            sortField,
            sortDirection,
            filters: buildFilters()
        };

        CheckService.getAllTemplates(searchObject)
            .then((data) => {
                let matchedTemplates = data.data || [];
                if (!scheduleModel.adHoc) {
                    matchedTemplates = matchedTemplates.filter(template => {
                        return !template.subCategory?.key || template.subCategory?.key !== TemplateSubCategories.WALKAROUNDCHECK;
                    });
                }
                setTableRows(matchedTemplates);
                setTotalRecords(data.totalCount || (data.data ? data.data.length : 0));
                validateSelectedChecks(data.data || []);
            })
            .catch(() => {
                setTableRows([]);
                setTotalRecords(0);
            })
            .finally(() => {
                setIsLoadingData(false);
            });
    }



    function getAssetTypeNames(assetTypeIds) {
        if (!Array.isArray(assetTypeIds) || assetTypeIds.length === 0) return "All";
        return assetTypeIds
            .map(id => {
                const type = assetTypes.find(t => t.id === id);
                return type ? type.name : "";
            })
            .filter(name => name)
            .join(", ");
    }

    function getAssetSubtypeNames(assetTypeIds, assetSubtypeIds) {
        if (!Array.isArray(assetSubtypeIds) || !Array.isArray(assetTypeIds) || assetSubtypeIds.length === 0) return "All";

        const subtypes = assetTypeIds
            .map(typeId => {
                const type = assetTypes.find(t => t.id === typeId);
                return type && Array.isArray(type.subtypes) ? type.subtypes : [];
            })
            .flat();

        return assetSubtypeIds
            .map(subtypeId => {
                const subtype = subtypes.find(st => st.id === subtypeId);
                return subtype ? subtype.name : "";
            })
            .filter(name => name)
            .join(", ");
    }


    function onTemplateCheckboxChange(event, template) {
        const checked = event.target.checked;
        
        setChecksModel(prev => {
            const clonedRelatedChecks = [];

            if (checked) {
                clonedRelatedChecks.push({id: template.templateId, name: template.templateName });
            } 

            const updated = { ...prev, relatedTemplates: clonedRelatedChecks };
            onModelUpdate(step, updated);

            return updated;
        });
    }

    function validate() {
        let validationRules = [];

        if (checksModel.enableChecks) {
            validationRules.push({
                fieldName: "relatedTemplates",
                required: true,
                custom: (m) => Array.isArray(m.relatedTemplates) && m.relatedTemplates.length > 0,
                message: "Please select at least one associated check."
            });
        }

        validateChecksModel(validationRules, checksModel);
    }

    // Use this to handle input value changes
    function onValueChange(fieldName, value) {
        let updatedModel = {
            ...checksModel,
            [fieldName]: value
        };

        if (fieldName === "enableChecks" && value === false) {
            updatedModel.relatedTemplates = [];
        }

        setChecksModel(updatedModel);
        onModelUpdate(step, updatedModel);
    }

    function setModel(changeModel) {
        setOriginalChecksModel(changeModel);
        setChecksModel(changeModel);
    }

    function onConfirmLeave(redirectToPath) {
        setModel(originalChecksModel);
        onModelUpdate(step, originalChecksModel);

        setTimeout(() => navigate(redirectToPath));
    }

    return (
        <UserAccess perform={fmsManageAction}
            yes={() => (
                <UserAccess
                    perform={"Checks:Manage"}
                    yes={() => (
                        <Fragment>
                            <ShowHide
                                evaluator={!isInWizardMode}
                                show={(
                                    <Fragment>
                                        <h2>Event Checks</h2>
                                        <AlertUnsavedData isDirty={isChecksModelDirty} onConfirmLeave={onConfirmLeave} />
                                    </Fragment>
                                )}
                            />

                            <NotificationInline
                                id="notification-employee-assessments"
                                loading={loading}
                                show={specialProcessing}
                                text={specialProcessingMessage}
                                intent="info"
                                allowClose={false}>
                            </NotificationInline>

                            <FormSwitch
                                disabled={saving || specialProcessing}
                                loading={loading}
                                label="Enable associated check"
                                helperText=" "
                                checked={checksModel.enableChecks}
                                onChange={e => onValueChange("enableChecks", e.target.checked)}
                            />

                            <ShowHide
                                evaluator={checksModel.enableChecks}
                                show={(
                                    <Fragment>
                                        <h3>Associated Check</h3>
                                        <h4>Please select from the list below of qualifying checks</h4>
                                        <div className="intro-text">
                                            <p>If a check doesn't appear here it's because it has already been assigned or doesn't match the Asset settings of this Event Type.</p>
                                        </div>
                                        <div className = "spacer-bottom">
                                            <ListingTable
                                                id="listing-table-event-type-checks"
                                                headers={tableHeaders}
                                                data={tableRows.map(template => {
                                                    const assetTypeIds = template.additionalProperties?.Conditions?.AssetTypes || [];
                                                    const assetSubtypeIds = template.additionalProperties?.Conditions?.AssetSubTypes || [];
                                                    let row = [
                                                        template.templateName,
                                                        template.versionNumber,
                                                        getAssetTypeNames(assetTypeIds)
                                                    ];
                                                    if (accountUsesSubType) {
                                                        row.push(getAssetSubtypeNames(assetTypeIds, assetSubtypeIds));
                                                    };
                                                    row.push(<FormCheckbox
                                                        key={template.templateId}
                                                        checked={checksModel?.relatedTemplates?.some(x => x.id === template.templateId)}
                                                        onChange={e => onTemplateCheckboxChange(e, template)}
                                                        id={`template-checkbox-${template.templateId}`}
                                                    />);
                                                    return row;
                                                })}
                                                totalRecordCount={totalRecords}
                                                loadingData={isLoadingData}
                                                pageable={true}
                                                pageSize={pageSize}
                                                onPagingChange={(newPageNumber, newPageSize) => {
                                                    setPageNumber(newPageNumber);
                                                    setPageSize(newPageSize);
                                                }}
                                            />
                                        </div>
                                        
                                        <NotificationInline
                                            id="notification-employee-assessments"
                                            loading={isLoadingData}
                                            show={Array.isArray(model.relatedTemplates) && model.relatedTemplates.length === 0 && checksModel.enableChecks}
                                            text="A check must be associated with the event type if associated check is enabled."
                                            intent="info"
                                            allowClose={false}>
                                        </NotificationInline>
                                    </Fragment>
                                )}
                            />

                            <ShowHide
                                evaluator={!isInWizardMode}
                                show={(
                                    <ButtonSave
                                        loading={loading}
                                        onClick={() => onSave("checks")}
                                        disabled={saving}
                                        simpleDisabled={!isChecksModelDirty || !isValid}
                                    />	
                                )}
                            />
                        </Fragment>)}
                    no={() => (
                        <Fragment>
                            <h4>You do not have permission to configure associated checks, or Checks is not available.</h4>
                        </Fragment>)}
                    />
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

EventTypeChecks.propTypes = {
    model: PropTypes.object,
    detailsModel: PropTypes.object,
    scheduleModel: PropTypes.object,
    forceLoad: PropTypes.bool,
    isInWizardMode: PropTypes.bool,
    saving: PropTypes.bool.isRequired,
    globalLoading: PropTypes.bool,
    onValidationUpdate: PropTypes.func,
    onModelUpdate: PropTypes.func,
    onSave: PropTypes.func,
    onForceLoadFinished: PropTypes.func
};
